import { createServerFn } from "@tanstack/solid-start";
import { z } from "zod";
import { withSentrySpan } from "~/lib/sentry-span";
import { authFunctionMiddleware } from "~/server/middleware/auth";
import {
  CreateBankTransactionSchema,
  UpdateBankTransactionSchema,
} from "./bank-transaction.schema";
import type { BankTransactionItem } from "./bank-transaction.server";

export const BankTransactionIdInputSchema = z.object({
  id: z.string().trim().min(1),
  businessId: z.string().trim().min(1),
});

export const ListBankTransactionsInputSchema = z.object({
  businessId: z.string().trim().min(1),
  search: z.string().optional(),
  offset: z.number().int().min(0).optional(),
  limit: z.number().int().min(1).max(200).optional(),
  ids: z.array(z.string().trim().min(1)).max(500).optional(),
  status: z.enum(["pending", "approved", "skipped"]).optional(),
  sortBy: z.string().optional(),
  sortDir: z.enum(["asc", "desc"]).optional(),
  /** Facet filter: only transactions whose category is in this list. */
  category: z.array(z.string().trim().min(1)).max(100).optional(),
  /** Only transactions for these bank accounts. */
  bankAccountIds: z.array(z.string().trim().min(1)).max(50).optional(),
});

export const CreateBankTransactionInputSchema = z.object({
  businessId: z.string().trim().min(1),
  item: CreateBankTransactionSchema,
});

export const UpdateBankTransactionInputSchema = z.object({
  ...BankTransactionIdInputSchema.shape,
  updates: UpdateBankTransactionSchema,
});

export const listBankTransactions = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => ListBankTransactionsInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("bankTransactions.list", async () => {
      const { listBankTransactionsForUser } =
        await import("./bank-transaction.server");
      return listBankTransactionsForUser(context.user.id, data.businessId, {
        search: data.search,
        offset: data.offset,
        limit: data.limit,
        ids: data.ids,
        status: data.status,
        sortBy: data.sortBy,
        sortDir: data.sortDir,
        category: data.category,
        bankAccountIds: data.bankAccountIds,
      });
    });
  });

export const getBankTransaction = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => BankTransactionIdInputSchema.parse(i))
  .handler(async ({ context, data }): Promise<BankTransactionItem> => {
    return withSentrySpan("bankTransactions.get", async () => {
      const { getBankTransactionForUser } =
        await import("./bank-transaction.server");
      const result = await getBankTransactionForUser(
        context.user.id,
        data.businessId,
        data.id,
      );
      if (!result) throw Response.json({ error: "Not found" }, { status: 404 });
      return result;
    });
  });

export const createBankTransaction = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => CreateBankTransactionInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("bankTransactions.create", async () => {
      const { createBankTransactionForUser } =
        await import("./bank-transaction.server");
      return createBankTransactionForUser(
        context.user.id,
        data.businessId,
        data.item,
      );
    });
  });

export const updateBankTransaction = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => UpdateBankTransactionInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("bankTransactions.update", async () => {
      const { updateBankTransactionForUser } =
        await import("./bank-transaction.server");
      const result = await updateBankTransactionForUser(
        context.user.id,
        data.id,
        data.updates,
      );
      if (!result) throw Response.json({ error: "Not found" }, { status: 404 });
      return result;
    });
  });

export const deleteBankTransaction = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => BankTransactionIdInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("bankTransactions.delete", async () => {
      const { deleteBankTransactionForUser } =
        await import("./bank-transaction.server");
      const result = await deleteBankTransactionForUser(
        context.user.id,
        data.businessId,
        data.id,
      );
      if (!result) throw Response.json({ error: "Not found" }, { status: 404 });
      return result;
    });
  });

export const approveBankTransaction = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => BankTransactionIdInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("bankTransactions.approve", async () => {
      const { approveBankTransactionForUser } =
        await import("./bank-transaction.server");
      const result = await approveBankTransactionForUser(
        context.user.id,
        data.businessId,
        data.id,
      );
      if (!result) throw Response.json({ error: "Not found" }, { status: 404 });
      return result;
    });
  });

export const unapproveBankTransaction = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => BankTransactionIdInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("bankTransactions.unapprove", async () => {
      const { unapproveBankTransactionForUser } =
        await import("./bank-transaction.server");
      const result = await unapproveBankTransactionForUser(
        context.user.id,
        data.businessId,
        data.id,
      );
      if (!result) throw Response.json({ error: "Not found" }, { status: 404 });
      return result;
    });
  });

export const ListBankTransactionCategoriesInputSchema = z.object({
  businessId: z.string().trim().min(1),
});

export const listBankTransactionCategories = createServerFn({
  method: "GET",
})
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => ListBankTransactionCategoriesInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("bankTransactions.categories", async () => {
      const { listBankTransactionCategoriesForUser } =
        await import("./bank-transaction.server");
      return listBankTransactionCategoriesForUser(
        context.user.id,
        data.businessId,
      );
    });
  });
